import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * Table — semantic data table with INSOLECH styling.
 *
 * Composition:
 *   <Table>
 *     <TableHeader>
 *       <TableRow>
 *         <TableHead>Order</TableHead>
 *         <TableHead>Patient</TableHead>
 *       </TableRow>
 *     </TableHeader>
 *     <TableBody>
 *       <TableRow>
 *         <TableCell>...</TableCell>
 *       </TableRow>
 *     </TableBody>
 *   </Table>
 *
 * Sorting + filtering are delegated to TanStack Table at the consumer level.
 * This file is presentation-only.
 *
 * RTL: text alignment uses `text-start`/`text-end`. Sticky-column class
 * uses `inline-start` (logical).
 */

export const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & { wrapperClassName?: string }
>(({ className, wrapperClassName, ...props }, ref) => (
  // R15 — `wrapperClassName` lets the consumer make THIS div the
  // vertical scroller too (e.g. `max-h-[60vh] overflow-auto`). Without
  // that, sticky-thead inside the table doesn't work because the
  // sticky positioning context is this wrapper, but a wrapping
  // ancestor (CardBody, page) was the one actually scrolling.
  <div className={cn('relative w-full overflow-x-auto', wrapperClassName)}>
    <table
      ref={ref}
      // R15 — base table font drops from text-sm (14px) to text-[13px] for
      // tighter density per Rafael's "smaller font, more rows" feedback.
      className={cn('w-full caption-bottom text-[13px]', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('bg-sand-50 border-b border-border-subtle', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('divide-y divide-border-subtle', className)} {...props} />
));
TableBody.displayName = 'TableBody';

export const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'transition-colors duration-fast ease-out',
        'data-[state=selected]:bg-brand-50',
        'hover:bg-sand-50',
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

export const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'px-4 h-10 text-start align-middle font-medium text-text-muted text-xs uppercase tracking-wide',
      'whitespace-nowrap',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td ref={ref} className={cn('px-4 py-3 align-middle text-sm text-text-primary', className)} {...props} />
));
TableCell.displayName = 'TableCell';

export const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('mt-4 text-sm text-text-muted', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';
